import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {WarningRow, Portfolio, Art14Row } from '../lib/contract';
import { findingStatusKind } from '../lib/contract';
import { useResourceMapped } from '../lib/api';
import { mapPortfolio, mapWarning, mapArt14, type ApiPortfolio, type ApiWarningRow, type ApiArt14Row } from '../lib/adapt';
import { useSession, canManage } from '../lib/session';
import { useMembers } from '../lib/members';
import { CISA_JUSTIFICATIONS, VEX_STATUSES } from '../lib/cra';
import { Badge, Btn, Card, PageHead, SeverityBadge, Tabs } from '../components/ui/primitives';
import { BatchContentEditor, type BatchContentPatch } from '../components/BatchContentEditor';
import { NotApplicablePanel, type NotApplicableInput } from '../components/NotApplicablePanel';
import { apiFetch } from '../lib/api';
import { RELEASE_STATUS_OPTIONS, TESTING_DUTIES, isTesting, statusBadge, type ReleaseStatus } from '../lib/release-status';
import { FrictionPanel, useToast } from '../components/ui/widgets';

const USE_FIXTURES_PAGE = (import.meta.env.VITE_USE_FIXTURES as string | undefined) !== 'false';

// ── VEX consequence copy (ported from console-mockup initVexEditor CONSEQ) ──
const VEX_CONSEQ: Record<string, { text: string; color: string }> = {
  not_affected: { text: 'Removes this warning from the applicable list', color: 'var(--teal)' },
  affected: { text: 'Keeps this as an applicable, open finding', color: 'var(--medium)' },
  fixed: { text: 'Marks the finding resolved — it was affected and is now remediated', color: 'var(--safe)' },
  under_investigation: { text: 'Keeps the finding open while you investigate', color: 'var(--muted)' },
};

// KEV first, then EPSS desc (null EPSS sorts last). The DEFAULT column order.
function sortFindings(rows: WarningRow[]): WarningRow[] {
  return [...rows].sort((a, b) => {
    if (a.isKev !== b.isKev) return a.isKev ? -1 : 1;
    return (b.epss ?? -1) - (a.epss ?? -1);
  });
}

// ── Findings filter + column ordering (user request 2026-07-23) ──
type FSortCol = 'default' | 'cve' | 'component' | 'severity' | 'epss';
const SIGNAL_CHIPS = [
  { k: 'all', label: 'All' },
  { k: 'kev', label: 'KEV · Art.14' },
  { k: 'epss', label: 'High-EPSS' },
  { k: 'eol', label: 'EOL' },
  { k: 'reach', label: 'reachable?' },
] as const;
type FChip = (typeof SIGNAL_CHIPS)[number]['k'];

function signalBadges(w: WarningRow): ReactNode[] {
  const out: ReactNode[] = [];
  if (w.isKev) {
    out.push(<Badge key="kev" kind="kev">[K] KEV</Badge>);
    out.push(<Badge key="art14" kind="med">Art.14</Badge>);
  }
  if (w.eol) out.push(<Badge key="eol" kind="high">EOL</Badge>);
  if (w.applicability === 'uncertain') out.push(<Badge key="reach" kind="out">reachable?</Badge>);
  return out;
}

// ── Inline VEX editor (NON-KEV row → full not-affected flow) ──
function VexEditor({ cve, product, onClose, onSubmit, showToast }: { cve: string; product: string; onClose: () => void; onSubmit: (data: { vexStatus: string; code: string; just: string; impact: string; remed: string }) => Promise<boolean>; showToast: (m: string) => void }) {
  const [status, setStatus] = useState('');
  const [code, setCode] = useState('');
  const [just, setJust] = useState('');
  const [impact, setImpact] = useState('');
  const [remed, setRemed] = useState('');
  const [busy, setBusy] = useState(false);

  const na = status === 'not_affected';

  // Never a silent dead-end — tell the user WHAT is missing (user report 2026-07-20).
  function whatsMissing(): string | null {
    if (!status) return 'Pick a VEX status first.';
    const need: string[] = [];
    if (just.trim().length < 10) need.push('a justification (at least 10 characters)');
    if (na) {
      if (!code) need.push('a CISA justification code');
      if (impact.trim().length < 10) need.push('an impact statement (at least 10 characters)');
    }
    return need.length ? `Before writing this VEX, add: ${need.join('; ')}.` : null;
  }
  const conseq = status ? VEX_CONSEQ[status] : null;

  return (
    <div className="vex-panel">
      <div className="vex-q">Does <span className="mono">{cve}</span> affect {product}?</div>

      <div className="vex-row">
        <span className="vex-label">VEX status</span>
        <div className="vex-opts" role="radiogroup">
          {VEX_STATUSES.map((s) => (
            <label className="vopt" key={s.value}>
              <input
                type="radio"
                name="vexstat-sbom"
                value={s.value}
                checked={status === s.value}
                onChange={() => setStatus(s.value)}
              />
              <span>{s.label}</span>
            </label>
          ))}
        </div>
      </div>

      {na && (
        <div className="vex-row">
          <span className="vex-label">
            CISA justification <span className="req">*</span>{' '}
            <span className="hint">— the code that reaches the regulator (no default)</span>
          </span>
          <select className="select" value={code} onChange={(e) => setCode(e.target.value)}>
            <option value="">Select a justification…</option>
            {CISA_JUSTIFICATIONS.map((j) => (
              <option key={j.code} value={j.code}>{j.code} — {j.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="vex-row">
        <span className="vex-label">
          Justification <span className="req">*</span>{' '}
          <span className="hint">— a named reason; no dismissal without one</span>
        </span>
        <textarea
          className="input"
          rows={2}
          value={just}
          onChange={(e) => setJust(e.target.value)}
          placeholder="e.g. vulnerable code path not reachable in our firmware build"
        />
      </div>

      {na && (
        <div className="vex-row">
          <span className="vex-label">Impact statement <span className="req">*</span></span>
          <textarea
            className="input"
            rows={2}
            value={impact}
            onChange={(e) => setImpact(e.target.value)}
            placeholder="e.g. the vulnerable function is excluded from this build (.config line 2847)"
          />
        </div>
      )}

      {status === 'fixed' && (
        <div className="vex-row">
          <span className="vex-label">Remediation <span className="hint">(optional)</span></span>
          <textarea
            className="input"
            rows={2}
            value={remed}
            onChange={(e) => setRemed(e.target.value)}
            placeholder="e.g. patched in 3.4.1 shipped to all placed-on-market units"
          />
        </div>
      )}

      {conseq && (
        <div className="vex-conseq" style={{ background: `color-mix(in srgb, ${conseq.color} 9%, transparent)` }}>
          <b>Consequence:</b> {conseq.text}.
        </div>
      )}

      <div className="toolbar" style={{ marginTop: 6 }}>
        <Btn variant="primary" disabled={busy} onClick={async () => {
          const miss = whatsMissing();
          if (miss) { showToast(miss); return; } // tell the user WHAT to fix, never nothing
          setBusy(true);
          const done = await onSubmit({ vexStatus: status, code, just, impact, remed });
          if (!done) setBusy(false); // on success the parent closes this editor — avoid setState-after-unmount
        }}>
          {busy ? 'Writing…' : na ? 'Write not-affected VEX' : 'Write VEX statement'}
        </Btn>
        <Btn onClick={onClose}>Cancel</Btn>
      </div>
    </div>
  );
}

export function SbomAnalysis() {
  const { id, vid } = useParams<{ id: string; vid: string }>();
  const { session } = useSession();
  // fresh=1 bypasses the 60s read cache so a just-completed analysis (finished by
  // the worker, which can't invalidate the web cache) shows its findings on the
  // next poll instead of after the TTL. This is the analysis page — always live.
  const portfolio = useResourceMapped<ApiPortfolio, Portfolio>(session, '/api/portfolio?fresh=1', mapPortfolio, {
    pollWhile: (p) => p.products.some((pr) => pr.versions.some((v) => v.id === vid && v.sbomStatus === 'pending')),
  });
  // Version-scoped findings (P3). Fixtures ignore the query string (path router
  // strips it) and fall back to name-matching below.
  // RULE: applicable until proven otherwise — the API default now includes
  // 'uncertain'. The Discarded tab shows what a config/VEX actually excluded.
  const warnings = useResourceMapped<ApiWarningRow[], WarningRow[]>(
    session,
    vid ? `/api/warnings?versionId=${vid}&fresh=1` : '/api/warnings?fresh=1',
    (rows) => rows.map(mapWarning),
  );
  const discardedRes = useResourceMapped<ApiWarningRow[], WarningRow[]>(
    session,
    vid ? `/api/warnings?versionId=${vid}&discarded=1&fresh=1` : '/api/warnings?discarded=1&fresh=1',
    (rows) => rows.map(mapWarning),
  );
  // Art.14 incidents for this org — used to deep-link each KEV finding to its
  // report (a KEV was "only shown" before, with no way to open its incident).
  const art14 = useResourceMapped<ApiArt14Row[], Art14Row[]>(session, '/api/art14?fresh=1', (rows) => rows.map(mapArt14));
  const { members } = useMembers(session.organizationId);
  const [findingsTab, setFindingsTab] = useState<'open' | 'discarded'>('open');
  // Findings filter (text + signal chips), column ordering, and multi-select batch.
  const [fQuery, setFQuery] = useState('');
  const [fChip, setFChip] = useState<FChip>('all');
  const [sortCol, setSortCol] = useState<FSortCol>('default');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selF, setSelF] = useState<Set<string>>(new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [naOpen, setNaOpen] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const isManager = canManage(session.role);
  const products = portfolio.data?.products ?? [];
  const product = products.find((p) => p.id === id) ?? products[0];
  const version = product?.versions.find((v) => v.id === vid) ?? product?.versions[0];

  const [vexFor, setVexFor] = useState<WarningRow | null>(null);
  const [toastNode, showToast] = useToast();
  const [phaseOpen, setPhaseOpen] = useState(false);
  const [status, setStatus] = useState<ReleaseStatus>('release');
  const [targetStatus, setTargetStatus] = useState<ReleaseStatus>('internal');
  useEffect(() => { if (version) { const st = version.releaseStatus ?? (version.release ? 'release' : 'internal'); setStatus(st); setTargetStatus(st === 'release' ? 'internal' : 'release'); } /* eslint-disable-line react-hooks/exhaustive-deps */ }, [version?.id]);
  // When the polled version flips out of "pending", pull the findings in (the
  // warnings/discarded resources don't poll themselves).
  const versionStatus = version?.sbomStatus;
  useEffect(() => {
    if (versionStatus === 'complete' || versionStatus === 'indeterminate') { warnings.reload(); discardedRes.reload(); art14.reload(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload is stable; fire on status transition only
  }, [versionStatus]);
  const release = status === 'release';
  // PERSISTED phase change (was local-only state — another dead control).
  async function confirmStatus() {
    if (!USE_FIXTURES_PAGE) {
      const r = await apiFetch(session, '/api/portfolio', {
        method: 'PATCH',
        body: JSON.stringify({ versionId: version!.id, patch: { releaseStatus: targetStatus } }),
      });
      if (!r.ok) { setPhaseOpen(false); return; }
    }
    setStatus(targetStatus);
    setPhaseOpen(false);
  }

  if (portfolio.loading) return <div className="hint" style={{ padding: 24 }}>Loading analysis…</div>;
  if (!product || !version) {
    return (
      <div className="hint" style={{ padding: 24 }}>
        No analysed version here yet. <Link to="/app/products">Back to Products →</Link>
      </div>
    );
  }

  const shortName = product.name.split(' ').pop() ?? product.name;
  // Findings scoped to THIS version: prefer the stable versionId (live) and fall
  // back to product+version name-matching for rows that don't carry one (fixtures).
  const scoped = (warnings.data ?? []).filter((w) =>
    w.versionId != null
      ? w.versionId === version.id
      : (w.productName === null || w.productName === product.name) && (w.versionLabel === null || w.versionLabel === version.label),
  );
  const findings = sortFindings(scoped);
  const discarded = (discardedRes.data ?? []).filter((w) =>
    w.versionId != null
      ? w.versionId === version.id
      : (w.productName === null || w.productName === product.name) && (w.versionLabel === null || w.versionLabel === version.label),
  );
  const nKev = scoped.filter((w) => w.isKev).length;
  // RULE: applicable until proven otherwise — every open finding counts.
  const nApplicable = scoped.length;
  const nHighEpss = scoped.filter((w) => (w.epss ?? 0) >= 0.5).length;
  const nEol = scoped.filter((w) => w.eol).length;

  // A KEV finding raises an Art.14 incident — map cveId → incident id for THIS
  // product-version so the row links straight to its report.
  const incidentByCve = new Map(
    (art14.data ?? [])
      .filter((a) => a.productName === product.name && a.versionLabel === version.label)
      .map((a) => [a.cveId, a.id] as const),
  );

  // ── Filter (text + signal chips) and column ordering (user request 2026-07-23) ──
  function applyView(list: WarningRow[]): WarningRow[] {
    const q = fQuery.trim().toLowerCase();
    const filtered = list.filter((w) => {
      if (fChip === 'kev' && !w.isKev) return false;
      if (fChip === 'epss' && !((w.epss ?? 0) >= 0.5)) return false;
      if (fChip === 'eol' && !w.eol) return false;
      if (fChip === 'reach' && w.applicability !== 'uncertain') return false;
      if (q && ![w.cveId, w.component, w.componentVersion].some((v) => v?.toLowerCase().includes(q))) return false;
      return true;
    });
    if (sortCol === 'default') return sortFindings(filtered);
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (w: WarningRow): string | number =>
      sortCol === 'cve' ? (w.cveId ?? '')
        : sortCol === 'component' ? `${w.component} ${w.componentVersion ?? ''}`
          : sortCol === 'severity' ? (w.cvss ?? -1)
            : (w.epss ?? -1); // 'epss'
    return [...filtered].sort((a, b) => {
      const va = val(a), vb = val(b);
      return typeof va === 'number' && typeof vb === 'number' ? (va - vb) * dir : String(va).localeCompare(String(vb)) * dir;
    });
  }
  // Click a header: first click sorts by it (numbers desc, text asc); click again toggles.
  function setSort(col: Exclude<FSortCol, 'default'>): void {
    if (sortCol === col) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return; }
    setSortCol(col);
    setSortDir(col === 'cve' || col === 'component' ? 'asc' : 'desc');
  }
  const sortMark = (col: FSortCol): string => (sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const viewOpen = applyView(findings);
  const viewDiscarded = applyView(discarded);

  // ── Multi-select → shared report content on the selected KEV findings' Art.14 reports ──
  const toggleF = (id: string): void =>
    setSelF((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectableIds = viewOpen.map((w) => w.findingId);
  const allSel = selectableIds.length > 0 && selectableIds.every((id) => selF.has(id));
  const toggleAllF = (): void =>
    setSelF((s) => { const n = new Set(s); if (allSel) selectableIds.forEach((id) => n.delete(id)); else selectableIds.forEach((id) => n.add(id)); return n; });
  // Selected findings → the deduped Art.14 reports they map to (KEV findings only).
  const selReportIds = [...new Set(
    findings.filter((w) => selF.has(w.findingId) && w.cveId)
      .map((w) => incidentByCve.get(w.cveId!))
      .filter((x): x is string => !!x),
  )];
  function clearSelF(): void { setSelF(new Set()); setBatchOpen(false); setNaOpen(false); }

  /** POST the batch to the selected findings' deduped Art.14 reports. */
  async function postFindingsBatch(
    patch: BatchContentPatch & { action?: string; justificationCode?: string; justification?: string; impactStatement?: string },
    successMsg: (applied: number, total: number) => string,
  ): Promise<void> {
    if (selReportIds.length === 0) { showToast('None of the selected findings has an Art.14 report.'); return; }
    if (USE_FIXTURES_PAGE) { showToast(`Demo: would update ${selReportIds.length} report(s) — not saved in the demo.`); clearSelF(); return; }
    setBatchBusy(true);
    try {
      const r = await apiFetch(session, '/api/art14/batch', { method: 'POST', body: JSON.stringify({ ids: selReportIds, patch }) });
      if (!r.ok) { showToast(`Batch update failed (${r.status}).`); return; }
      const data = (await r.json()) as { applied: number; total: number };
      showToast(successMsg(data.applied, data.total));
      clearSelF();
      art14.reload(); warnings.reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBatchBusy(false);
    }
  }

  function applyFindingsBatch(patch: BatchContentPatch): void {
    if (Object.keys(patch).length === 0) { showToast('Fill at least one field to apply.'); return; }
    void postFindingsBatch(patch, (a, t) => `Wrote shared report content to ${a} of ${t} report(s).`);
  }
  function applyFindingsNotApplicable(data: NotApplicableInput): void {
    void postFindingsBatch({ action: 'not-applicable', ...data }, (a, t) => `Marked ${a} of ${t} report(s) not applicable.`);
  }

  function disposition(w: WarningRow): ReactNode {
    if (w.vexStatus === 'sent') return <Badge kind="safe">Sent</Badge>;
    if (w.vexStatus === 'not_affected') return <Badge kind="safe" data-testid="finding-not-affected">Not affected</Badge>;
    if (w.applicability === 'not_applicable') return <Badge kind="out">dismissed (VEX)</Badge>;
    const incidentId = w.isKev && w.cveId ? incidentByCve.get(w.cveId) : undefined;
    if (incidentId) {
      return <Link to={`/app/incidents/${incidentId}`} data-testid="open-incident" style={{ color: 'var(--teal)', fontWeight: 500 }}>Open incident →</Link>;
    }
    if (w.applicability === 'uncertain' && !w.isKev) {
      return <Btn variant="ghost" sm onClick={() => setVexFor((cur) => (cur?.findingId === w.findingId ? null : w))}>Set as not affected</Btn>;
    }
    return <Badge kind="warn">open</Badge>;
  }

  return (
    <>
      <PageHead
        title={`${shortName} ${version.label} — analysis`}
        sub={`SBOM · ${version.componentCount} components${version.sbomQuality !== null ? ` · quality ${version.sbomQuality}/100` : ''}`}
        crumbs={
          <>
            <Link to="/app/products">Products</Link> ／{' '}
            <Link to={`/app/products/${product.id}`}>{product.name}</Link> ／ {version.label} analysis
          </>
        }
      />

      <div className="stats">
        <div className="card stat">
          <div className="k">KEV-flagged</div>
          <div className="v crit">{nKev}</div>
          <div className="s"><Badge kind="kev" style={{ fontSize: 'calc(10px*var(--fs))' }}>Art.14 trigger</Badge></div>
        </div>
        <div className="card stat">
          <div className="k">Applicable CVEs</div>
          <div className="v warn">{nApplicable}</div>
          <div className="s">applicable until proven otherwise · {discarded.length} discarded</div>
        </div>
        <div className="card stat">
          <div className="k">High-EPSS CVEs</div>
          <div className="v">{nHighEpss}</div>
          <div className="s">EPSS ≥ 0.50 — likely to be exploited</div>
        </div>
        <div className="card stat">
          <div className="k">EOL components</div>
          <div className="v" style={{ color: 'var(--high)' }}>{nEol}</div>
          <div className="s">past end-of-life — unsupported</div>
        </div>
      </div>

      <div className="section-head" style={{ marginTop: 4 }}>
        <h3>Findings</h3>
        <span className="aside">click a column to sort · select KEV rows to write their reports together · uncertain shown as “reachable?”</span>
      </div>
      <Tabs
        tabs={[
          { key: 'open' as const, label: <>Findings <span className="n">{scoped.length}</span></> },
          { key: 'discarded' as const, label: <>Discarded <span className="n">{discarded.length}</span></> },
        ]}
        value={findingsTab} onChange={setFindingsTab}
      />

      {/* Filter: simple text search + signal chips (user request 2026-07-23). */}
      <div className="filters" style={{ marginTop: 10 }}>
        <div className="search"><span>⌕</span>
          <input placeholder="Filter by CVE or component…" data-testid="findings-search" value={fQuery} onChange={(e) => setFQuery(e.target.value)} />
        </div>
        {SIGNAL_CHIPS.map((c) => (
          <button key={c.k} className={`chip${fChip === c.k ? ' on' : ''}`} data-testid={`fchip-${c.k}`} onClick={() => setFChip(c.k)}>{c.label}</button>
        ))}
      </div>

      {/* Batch bar + report editor — only on the open Findings tab. */}
      {findingsTab === 'open' && selF.size > 0 && (
        <div style={{ margin: '10px 0' }}>
          <div className="toolbar" data-testid="findings-batch-bar"
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 12px', background: 'var(--panel, var(--card-bg))', border: '1px solid var(--border)', borderRadius: 8 }}>
            <b data-testid="findings-batch-count" style={{ fontSize: 'calc(13px*var(--fs))' }}>{selF.size} selected</b>
            <span className="hint" data-testid="findings-batch-reports">{selReportIds.length} with an Art.14 report</span>
            <Btn sm variant="primary" data-testid="findings-batch-edit" disabled={selReportIds.length === 0} onClick={() => { setBatchOpen((o) => !o); setNaOpen(false); }}>
              {batchOpen ? 'Close editor' : `Write ${selReportIds.length} report${selReportIds.length === 1 ? '' : 's'} together…`}
            </Btn>
            {isManager && (
              <Btn sm data-testid="findings-batch-not-applicable" disabled={selReportIds.length === 0} onClick={() => { setNaOpen((o) => !o); setBatchOpen(false); }}>
                {naOpen ? 'Close' : 'Does not apply…'}
              </Btn>
            )}
            <Btn sm disabled={batchBusy} data-testid="findings-batch-clear" onClick={clearSelF}>Clear</Btn>
          </div>
          {batchOpen && selReportIds.length > 0 && (
            <BatchContentEditor
              count={selReportIds.length}
              busy={batchBusy}
              members={members}
              onApply={applyFindingsBatch}
              onCancel={() => setBatchOpen(false)}
              note={<>Applies to the {selReportIds.length} selected finding{selReportIds.length === 1 ? '' : 's'} that carry an Art.14 report (KEV). Non-KEV rows are skipped.</>}
            />
          )}
          {naOpen && selReportIds.length > 0 && (
            <NotApplicablePanel
              count={selReportIds.length}
              busy={batchBusy}
              onApply={applyFindingsNotApplicable}
              onCancel={() => setNaOpen(false)}
              note="Applies to the selected KEV findings that carry an Art.14 report; non-KEV rows are skipped."
            />
          )}
        </div>
      )}

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              {findingsTab === 'open' && (
                <th style={{ width: 34 }}>
                  <input type="checkbox" aria-label="Select all" data-testid="finding-select-all" checked={allSel} onChange={toggleAllF} />
                </th>
              )}
              <th data-testid="fsort-cve" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setSort('cve')}>CVE{sortMark('cve')}</th>
              <th data-testid="fsort-component" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setSort('component')}>Component{sortMark('component')}</th>
              <th data-testid="fsort-severity" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setSort('severity')}>Severity{sortMark('severity')}</th>
              <th>Signals</th>
              <th className="r" data-testid="fsort-epss" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setSort('epss')}>EPSS{sortMark('epss')}</th>
              <th>Disposition</th>
            </tr>
          </thead>
          <tbody data-testid={findingsTab === 'open' ? 'findings-rows' : 'discarded-rows'}>
            {(findingsTab === 'open' ? viewOpen : viewDiscarded).map((w) => {
              const isSel = selF.has(w.findingId);
              return (
                <tr key={w.findingId} className={isSel ? 'sel' : undefined} data-testid="finding-row">
                  {findingsTab === 'open' && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={`Select ${w.cveId ?? w.component}`} data-testid="finding-select" checked={isSel} onChange={() => toggleF(w.findingId)} />
                    </td>
                  )}
                  <td className="mono">{w.cveId ?? '—'}</td>
                  <td className="mono">{w.component}{w.componentVersion ? ` ${w.componentVersion}` : ''}</td>
                  <td><SeverityBadge kind={findingStatusKind(w)} /></td>
                  <td><span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>{signalBadges(w)}</span></td>
                  <td className="mono r">{w.epss != null ? w.epss.toFixed(2) : '—'}</td>
                  <td>{findingsTab === 'discarded'
                    ? <Badge kind="out">{w.vexStatus ? 'excluded — VEX' : 'excluded — build config'}</Badge>
                    : disposition(w)}</td>
                </tr>
              );
            })}
            {(findingsTab === 'open' ? viewOpen : viewDiscarded).length === 0 && (
              <tr><td colSpan={findingsTab === 'open' ? 7 : 6} className="hint" style={{ padding: 16, textAlign: 'center' }}>No findings match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {findingsTab === 'discarded' && discarded.length === 0 && (
        <p className="hint" data-testid="discarded-empty" style={{ marginTop: 8 }}>
          Nothing discarded — no build config or VEX has proven any finding inapplicable yet.
          Everything stays in Findings until then.
        </p>
      )}
      {(() => {
        // Honest triage hint — computed, never a fixture number.
        const nUncertain = scoped.filter((w) => w.applicability === 'uncertain' && !w.isKev).length;
        return nUncertain > 0 ? (
          <div className="foot-note" data-testid="uncertain-note">
            {nUncertain} finding{nUncertain === 1 ? '' : 's'} marked “reachable?” — no build config yet, so reachability is unproven.
            Upload a .config to auto-triage likely false positives, or write per-CVE VEX statements.
          </div>
        ) : null;
      })()}

      {vexFor && (
        <VexEditor
          cve={vexFor.cveId ?? '—'}
          product={`${product.name} ${version.label}`}
          showToast={showToast}
          onClose={() => setVexFor(null)}
          onSubmit={async ({ vexStatus, code, just, impact, remed }) => {
            // LIVE: persist the VEX against this finding. This editor is the NON-KEV path
            // (KEV rows link out to their incident), so no Art.14 seam is needed here.
            if (!USE_FIXTURES_PAGE) {
              const r = await apiFetch(session, '/api/vex', {
                method: 'POST',
                body: JSON.stringify({
                  action: 'set',
                  findingId: vexFor.findingId,
                  vexStatus,
                  justification: just,
                  ...(code ? { justificationCode: code } : {}),
                  ...(impact.trim() ? { impactStatement: impact } : {}),
                  ...(remed.trim() ? { remediation: remed } : {}),
                }),
              });
              if (!r.ok) {
                let msg = `VEX write failed (${r.status})`;
                try { const b = (await r.clone().json()) as { error?: string }; if (b.error) msg = b.error; } catch { /* keep status message */ }
                showToast(msg);
                return false;
              }
            }
            showToast('VEX statement written.');
            setVexFor(null);
            // The finding leaves "open" for the Discarded tab (or updates its disposition) — pull fresh.
            warnings.reload(); discardedRes.reload();
            return true;
          }}
        />
      )}
      {toastNode}

      <Card
        style={{ padding: 18, marginTop: 20, borderColor: 'color-mix(in srgb, var(--medium) 32%, var(--border))' }}
      >
        <div className="section-head">
          <h3>Version phase</h3>
          {(() => { const b = statusBadge(status); return <Badge kind={b.kind}>{b.text}</Badge>; })()}
        </div>
        <p className="hint" style={{ margin: '8px 0 0' }}>
          {release ? (
            'Release versions are covered by Art.14 monitoring — a KEV in this version triggers a mandatory 24h ENISA obligation.'
          ) : isTesting(status) ? (
            <span data-testid="testing-duties">⚠ {TESTING_DUTIES}</span>
          ) : (
            <>
              <b style={{ color: 'var(--critical)' }}>Not monitored.</b> This version is excluded from Art.14 — no alerts
              will be raised. You can revert this.
            </>
          )}
        </p>

        {!phaseOpen ? (
          <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
            <select className="select" style={{ width: 230 }} data-testid="phase-target" value={targetStatus}
              onChange={(e) => setTargetStatus(e.target.value as ReleaseStatus)}>
              {RELEASE_STATUS_OPTIONS.filter((o) => o.value !== status).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <Btn sm onClick={() => setPhaseOpen(true)}>Change phase…</Btn>
          </div>
        ) : (
          <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <FrictionPanel
              title={targetStatus === 'release' ? 'Switch to Release — monitored for Art.14' : `Switch to ${RELEASE_STATUS_OPTIONS.find((o) => o.value === targetStatus)?.label ?? targetStatus} — excluded from Art.14`}
              tone={targetStatus === 'release' ? 'teal' : 'critical'}
              consequence={
                targetStatus === 'release' ? (
                  <>This version returns to <b>Art.14 monitoring</b> — a KEV affecting it will raise an obligation again.</>
                ) : isTesting(targetStatus) ? (
                  <>
                    <b>Testing release (CRA Recital 37).</b> {TESTING_DUTIES}{' '}
                    <b style={{ color: 'var(--critical)' }}>No Art.14 alerts will be raised for this version.</b>
                  </>
                ) : (
                  <>
                    <b style={{ color: 'var(--critical)' }}>This removes the version from Art.14 monitoring.</b> If it is in
                    fact placed on the EU market and a KEV later affects it, <b>you will not be alerted</b> and could miss a
                    mandatory 24-hour ENISA notification — a CRA breach carrying fines up to €15M or 2.5% of global turnover.
                  </>
                )
              }
              ackLabel={
                targetStatus === 'release'
                  ? 'I confirm this version should be monitored for Art.14 obligations.'
                  : isTesting(targetStatus)
                    ? 'I confirm this is a time-limited testing release, visibly marked non-compliant, released after a risk assessment — and I accept it is not Art.14-tracked.'
                    : 'I confirm this version is NOT placed on the EU market, and I accept that no Art.14 alerts will be raised for it.'
              }
              confirmLabel="Confirm phase change"
              onConfirm={() => void confirmStatus()}
              onCancel={() => setPhaseOpen(false)}
            />
          </div>
        )}
      </Card>
    </>
  );
}
